import { memo, useEffect, useState } from 'react'
import { Search } from 'lucide-react'
import { itemsApi, programsApi } from '../../lib/api.js'
import { formatDuration, isBroadcastableMedia, PROGRAM_TYPE_LABELS } from '../../lib/utils.js'
import { broadcastDayKey } from '../../lib/broadcast-day.js'
import { recordGridChange } from '../../lib/gridUndo.js'
import type { Program, ScheduleItem } from '../../types/index.js'
import { ModalShell } from '../common/ModalShell.js'
import { Spinner } from '../common/Spinner.js'
import './AgendaDialogs.css'

function hasBroadcastableMedia(program: Program): boolean {
  return Boolean(program.mediaAssets?.some(asset => !asset.isProxy && isBroadcastableMedia(asset.status)))
}

export const ReplaceJingleDialog = memo(function ReplaceJingleDialog(props: {
  item: ScheduleItem
  channelId: string
  onSaved: (item: ScheduleItem) => void
  onClose: () => void
}) {
  const [query, setQuery] = useState('')
  const [programs, setPrograms] = useState<Program[]>([])
  const [selectedProgram, setSelectedProgram] = useState<Program | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const timer = window.setTimeout(() => {
      setLoading(true)
      setError(null)
      programsApi.list({
        channelId: props.channelId,
        catalog: 'jingles',
        search: query.trim() || undefined,
        limit: 100,
        sortBy: 'title',
        sortDir: 'asc',
      }).then(result => {
        if (cancelled) return
        setPrograms((result.data ?? []).filter((program: Program) => (
          program.id !== props.item.programId &&
          !program.isLive &&
          program.durationSec === props.item.durationSec
        )))
      }).catch(cause => {
        if (!cancelled) setError(cause?.message ?? 'Impossible de charger les éléments d’antenne disponibles.')
      }).finally(() => {
        if (!cancelled) setLoading(false)
      })
    }, 250)
    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [props.channelId, props.item.durationSec, props.item.programId, query])

  const replace = async () => {
    if (!selectedProgram) return setError('Sélectionnez l’élément d’antenne de remplacement.')
    setBusy(true)
    setError(null)
    try {
      const updated = await recordGridChange({
        label:   `remplacement de ${props.item.program?.title ?? 'cet élément d’antenne'}`,
        gridIds: [props.item.gridId],
        date:    broadcastDayKey(props.item.startTime),
      }, () => itemsApi.replaceJingle(props.item.id, selectedProgram.id))
      props.onSaved(updated)
      props.onClose()
    } catch (cause: any) {
      setError(cause?.message ?? 'Impossible de remplacer cet élément d’antenne.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <ModalShell title="Remplacer un élément d’antenne" onClose={props.onClose} closeDisabled={busy} size="medium" className="agenda-dialog agenda-dialog--replace">
      <div className="agenda-dialog__subtitle agenda-dialog__subtitle--ellipsis">
        Remplacer « {props.item.program?.title ?? 'Élément d’antenne'} » · durée conservée : {formatDuration(props.item.durationSec)}
      </div>

      <div className="agenda-replace__body">
        <section>
          <div className="agenda-dialog__section-label">Par un autre élément d’antenne</div>
          <div className="agenda-replace__search-wrap">
            <Search size={14} aria-hidden="true" className="agenda-replace__search-icon" />
            <input
              autoFocus
              className="tp-input agenda-replace__search"
              placeholder="Rechercher un titre…"
              value={query}
              onChange={event => {
                setQuery(event.target.value)
                setSelectedProgram(null)
              }}
            />
          </div>
          <div className="agenda-replace__program-list">
            {loading && <div className="agenda-dialog__empty"><Spinner /> Chargement…</div>}
            {!loading && programs.length === 0 && (
              <div className="agenda-dialog__empty">
                Aucun autre élément d’antenne de {formatDuration(props.item.durationSec)} trouvé.
              </div>
            )}
            {!loading && programs.map(program => {
              const usable = hasBroadcastableMedia(program)
              const selected = selectedProgram?.id === program.id
              return <button
                key={program.id}
                type="button"
                disabled={!usable || busy}
                aria-pressed={selected}
                onClick={() => setSelectedProgram(program)}
                className={`agenda-replace__program ${selected ? 'is-selected' : ''}`}
              >
                <span className="agenda-replace__program-main">
                  <span className="agenda-replace__program-title">{program.title}</span>
                  <span className={`agenda-replace__program-meta ${usable ? '' : 'is-warning'}`}>
                    {usable ? PROGRAM_TYPE_LABELS[program.type] : 'Média indisponible'}
                  </span>
                </span>
                <span className="agenda-replace__program-duration">{formatDuration(program.durationSec)}</span>
              </button>
            })}
          </div>
        </section>

        <div className="agenda-dialog__notice">
          Le créneau et sa durée restent inchangés ; seul l’élément d’antenne diffusé est remplacé.
        </div>
        {error && <div role="alert" className="agenda-dialog__error">{error}</div>}
      </div>

      <footer className="tp-modal-actions agenda-replace__actions">
        <button className="tp-button" disabled={busy} onClick={props.onClose}>Annuler</button>
        <button className="tp-button tp-button--primary" disabled={!selectedProgram || busy} data-state={busy ? 'loading' : undefined} onClick={() => { replace().catch(console.error) }}>
          {busy ? 'Remplacement…' : 'Remplacer'}
        </button>
      </footer>
    </ModalShell>
  )
})
